import { nanoid } from "nanoid";
import React, { useCallback, useRef } from "react";

import { useExplorationDrawerState } from "../react/drawer/use-exploration-drawer-state";
import { DONT_AUTO_DISMISS, notification } from "../react/notification/Notification";

import { NotificationErrorMessage } from "./_notification-error-message";

interface ErrorDetails {
	message: string;
	error?: unknown;
	onRetry?: VoidFunction;
}

export interface UseErrorMessageOutput {
	displayErrorMessage: (errorDetails: ErrorDetails) => void;
	clearErrorMessage: () => void;
}

/**
 * Displays error in popup notifications.
 * Supports a "Read more" link to open a drawer with detailed error information
 * and a "Retry" option for reattempting the action.
 */
export const useErrorMessage = (): UseErrorMessageOutput => {
	const explorationDrawer = useExplorationDrawerState();
	const errorMessageId = useRef<string>(undefined);

	const clearErrorMessage = useCallback(() => {
		if (errorMessageId.current != null) {
			notification.close(errorMessageId.current);
			errorMessageId.current = undefined;
		}
	}, []);

	const displayErrorMessage = ({ message, error, onRetry }: ErrorDetails) => {
		clearErrorMessage();
		errorMessageId.current = nanoid();

		notification.error({
			key: errorMessageId.current,
			duration: DONT_AUTO_DISMISS,
			message: (
				<NotificationErrorMessage
					error={error}
					message={message}
					onReadMore={() => {
						clearErrorMessage();
						explorationDrawer.openErrorDetails({ error });
					}}
					onRetry={
						onRetry == null
							? undefined
							: () => {
									clearErrorMessage();
									onRetry();
								}
					}
				/>
			),
		});
	};

	return {
		displayErrorMessage,
		clearErrorMessage,
	};
};
